/**
 * Read a mould list out of a spreadsheet, and say exactly what each column is
 * used for.
 *
 * This module only reads. It turns a file into rows and pictures; deciding
 * what those rows mean for the catalog - new, already there, deleted and coming
 * back, a value to keep or replace - is the server's job (POST /imports), so
 * that "imported" is decided against the database, never against this device's
 * copy of it.
 *
 * What it reads, compared with before:
 *   - every sheet, not only the first. The Kandivali file's 400 moulds were on
 *     its second sheet, behind a first sheet of ten;
 *   - every picture on a row, not only the first. Patalganga has 679 photos
 *     on 377 moulds, up to ten on one;
 *   - without ExcelJS, which needed about 3 GB of memory for the Patalganga
 *     workbook - more than a browser tab is given.
 *
 * Columns are matched by heading text, never by position: the client's sheets
 * move columns around between versions, and the two sheets of one file can put
 * the heading on different rows. The mapping is shown to the person importing,
 * every column either "imported as" a field or "ignored" with the reason, and
 * any choice can be changed - the browser remembers it for next time.
 */
import {
  isOleFile,
  isPlaceholderCode,
  parseCsv,
  parseXls,
  readWorkbook,
  type CellValue,
  type ImportMasterField,
  type ImportRowInput,
  type SheetImage,
} from '@mouldtrack/shared';

// --- fields ---------------------------------------------------------------

export type ImportField =
  | 'code'
  | 'mouldName'
  | 'codeAndName'
  | 'cavities'
  | 'moldSize'
  | 'manufactureYear'
  | 'qrNumber'
  | 'toolTransfer'
  | 'notes'
  | 'location'
  | ImportMasterField;
export type ColumnUse = ImportField | 'ignore';

interface FieldDef {
  field: ImportField;
  label: string;
  /** Normalised headings that mean this field (see `headingKey`). */
  aliases: string[];
}

/** Every field a column can be imported as, in the order the picker lists them. */
export const IMPORT_FIELDS: FieldDef[] = [
  { field: 'code', label: 'Mould code', aliases: ['moldnumber', 'mouldnumber', 'moldcode', 'mouldcode', 'moldno', 'mouldno'] },
  { field: 'mouldName', label: 'Mould name', aliases: ['moldname', 'mouldname'] },
  { field: 'codeAndName', label: 'Mould code and name (one cell)', aliases: ['moldcodeandname', 'mouldcodeandname'] },
  // CODE AND NAME is the Master Sheet's project column ("E9707-HUNGRY HUNGRY HIPPO LAUNCHER").
  { field: 'project', label: 'Project', aliases: ['projectcodeandname', 'project', 'projectname', 'codeandname'] },
  { field: 'customer', label: 'Customer', aliases: ['customer', 'customername'] },
  { field: 'ownerCompany', label: 'Owner', aliases: ['mouldownership', 'moldownership', 'moldowner', 'mouldowner', 'owner'] },
  { field: 'manufactureCompany', label: 'Manufacturer', aliases: ['manufacturer', 'manufacturingcompany', 'manufacturecompany'] },
  { field: 'toolType', label: 'Tool type', aliases: ['tooltype'] },
  { field: 'cavities', label: 'Cavities', aliases: ['noofcavities', 'cavities', 'numberofcavities'] },
  { field: 'materialGrade', label: 'Material', aliases: ['materialshrinkage', 'materialgrade'] },
  { field: 'runnerType', label: 'Runner type', aliases: ['runnertype'] },
  { field: 'gateType', label: 'Gate type', aliases: ['gatetype'] },
  { field: 'ejectionType', label: 'Ejection type', aliases: ['ejectiontype'] },
  {
    field: 'mechanismType',
    label: 'Sliders / lifters',
    aliases: [
      'noofslidersandliftersanditsmeshanism',
      'noofslidersandliftersanditsmechanism',
      // The Kandivali sheet, the default format since 8 Oct 2026.
      'noofslidersandlifters',
      'slidersandlifters',
    ],
  },
  { field: 'coreCavityMaterial', label: 'Core / cavity material', aliases: ['corecavitymaterial'] },
  { field: 'moldBaseMaterial', label: 'Mould base material', aliases: ['moldbasematerial', 'mouldbasematerial'] },
  {
    field: 'moldSize',
    label: 'Mould size',
    // Engraved as "Mold Dimension:" on the Design B plate. The client's sheets
    // head it "MOLD SIZE (LxBxH)".
    aliases: [
      'moldsize',
      'mouldsize',
      'moldsizelxbxh',
      'mouldsizelxbxh',
      'moldsizelxwxh',
      'mouldsizelxwxh',
      'molddimension',
      'moulddimension',
      'molddimensions',
      'moulddimensions',
    ],
  },
  { field: 'designer', label: 'Designer', aliases: ['designername', 'designer'] },
  {
    field: 'manufactureYear',
    label: 'Manufacturing year',
    // A sheet with a full date in this column gives its year; the day and
    // month are not kept - the catalog records the year only.
    aliases: [
      'manufacturetransferredyear',
      'manufacturetransferred',
      'yearofmanufacture',
      'manufactureyear',
      'manufacturingyear',
      'manufacturedate',
      'manufacturingdate',
      'dateofmanufacture',
      'mfgyear',
      'mfgdate',
    ],
  },
  { field: 'qrNumber', label: 'QR number', aliases: ['qrnumber', 'qrno'] },
  { field: 'toolTransfer', label: 'Tool transfer (yes / no)', aliases: ['tooltransfer', 'tooltransferred', 'transferred'] },
  { field: 'notes', label: 'Notes', aliases: ['remarks', 'remark', 'notes', 'note', 'comments'] },
  {
    field: 'location',
    label: 'Location (per row)',
    aliases: ['moldlocation', 'mouldlocation', 'location', 'currentlocation'],
  },
];

const FIELD_BY_ALIAS = new Map(IMPORT_FIELDS.flatMap((f) => f.aliases.map((a) => [a, f.field] as const)));
export const FIELD_LABEL = Object.fromEntries(IMPORT_FIELDS.map((f) => [f.field, f.label])) as Record<ImportField, string>;

const MASTER_FIELDS = new Set<ImportField>([
  'project',
  'customer',
  'ownerCompany',
  'manufactureCompany',
  'designer',
  'toolType',
  'runnerType',
  'gateType',
  'ejectionType',
  'mechanismType',
  'coreCavityMaterial',
  'moldBaseMaterial',
  'materialGrade',
]);

/**
 * Columns the client asked to leave out of the import. Ignored unless someone
 * switches them on in the column list.
 *
 * Empty since 28 Oct 2026. "MOLD SIZE (LxBxH)" was here until 24 Oct (it is the
 * mould size now, engraved as "Mold Dimension:") and "CODE AND NAME" until
 * 28 Oct (it is the project now). Both import without anyone switching them on.
 */
export const CLIENT_IGNORED: Record<string, string> = {};

/** Headings that are understood, and deliberately not imported as a field. */
export const NOT_IMPORTED: Record<string, string> = {
  image: 'Photos are taken from the pictures on each row',
  images: 'Photos are taken from the pictures on each row',
  photo: 'Photos are taken from the pictures on each row',
  sno: 'The catalog numbers moulds itself',
  srno: 'The catalog numbers moulds itself',
  qrcode: 'QR codes are made from the mould code',
};

/** "NO.OF CAVITIES" and "no of cavities" are the same heading. */
export function headingKey(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** What a heading is used for when nobody has said otherwise. */
export function defaultUse(key: string): ColumnUse {
  // A column the client asked to leave out stays out until someone picks a field for it.
  if (CLIENT_IGNORED[key]) return 'ignore';
  return FIELD_BY_ALIAS.get(key) ?? 'ignore';
}

/** "Yes", "Y", "NO", "n" - a yes/no cell. Anything else says nothing. */
function yesNo(text: string): boolean | null {
  const t = text.trim().toLowerCase();
  if (['yes', 'y', 'true', '1'].includes(t)) return true;
  if (['no', 'n', 'false', '0'].includes(t)) return false;
  return null;
}

export type HeadingStatus = 'required' | 'imported' | 'client-ignored' | 'not-imported' | 'unrecognised';

export function headingStatus(key: string, use: ColumnUse): { status: HeadingStatus; reason?: string } {
  if (use === 'code' || use === 'codeAndName') return { status: 'required' };
  if (use !== 'ignore') return { status: 'imported' };
  if (CLIENT_IGNORED[key]) return { status: 'client-ignored', reason: CLIENT_IGNORED[key] };
  if (NOT_IMPORTED[key]) return { status: 'not-imported', reason: NOT_IMPORTED[key] };
  if (FIELD_BY_ALIAS.has(key)) return { status: 'not-imported', reason: 'Switched off' };
  return { status: 'unrecognised', reason: 'Not a column this knows' };
}

const MAPPING_STORE = 'mt.import.mapping.v3';
/**
 * Earlier stores, newest first, with the headings whose default has changed
 * since: an "Ignore" saved for one of those was only the old default, and
 * keeping it would leave the column out exactly as before.
 */
const OLDER_STORES = [
  { store: 'mt.import.mapping.v2', ignoredUntilNow: ['codeandname'] },
  { store: 'mt.import.mapping.v1', ignoredUntilNow: ['codeandname', 'moldsizelxbxh', 'mouldsizelxbxh'] },
];

/** Choices people made on earlier imports, by heading. Browser-local and optional. */
export function savedMapping(): Record<string, ColumnUse> {
  try {
    const raw = localStorage.getItem(MAPPING_STORE);
    if (raw) return JSON.parse(raw) as Record<string, ColumnUse>;
    // Carried over from the newest earlier store, less the old defaults.
    for (const { store, ignoredUntilNow } of OLDER_STORES) {
      const old = localStorage.getItem(store);
      if (!old) continue;
      const mapping = JSON.parse(old) as Record<string, ColumnUse>;
      for (const key of ignoredUntilNow) if (mapping[key] === 'ignore') delete mapping[key];
      return mapping;
    }
    return {};
  } catch {
    return {};
  }
}

export function saveMapping(overrides: Record<string, ColumnUse>): void {
  try {
    localStorage.setItem(MAPPING_STORE, JSON.stringify(overrides));
  } catch {
    // private window or storage blocked - the defaults still apply next time
  }
}

// --- reading the file -------------------------------------------------------

export interface SourceHeading {
  col: number;
  text: string;
  key: string;
}

export interface SourceSheet {
  name: string;
  rowCount: number;
  /** 0 when no row in the first ten looks like a heading row. */
  headerRow: number;
  headings: SourceHeading[];
  cell(row: number, col: number): CellValue;
  images: Map<number, SheetImage[]>;
  /** Rows below the heading with anything in them. */
  dataRows: number;
  pictures: number;
}

export interface SourceFile {
  fileName: string;
  sheets: SourceSheet[];
}

interface RawSheet {
  name: string;
  rowCount: number;
  columnCount: number;
  cell(row: number, col: number): CellValue;
  images: Map<number, SheetImage[]>;
}

const MAX_COLUMNS = 60;

/**
 * A cell's text. The readers return primitives, but a value can still be a
 * number where text was meant ("4" cavities) or carry stray spacing.
 */
function cellText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toISOString();
  return '';
}

export const clean = (v: unknown): string => cellText(v).replace(/\s+/g, ' ').trim();

/**
 * Pick a reader by content first, extension second. A file called `.xlsx` that
 * is really a legacy .xls - which happens whenever somebody renames one - would
 * otherwise fail deep inside a zip reader with a message that helps nobody.
 */
/** Bigger than any mould list with photos the client has sent, by a wide margin. */
export const MAX_IMPORT_FILE_BYTES = 150 * 1024 * 1024;

/** Office's encrypted workbook: an OLE container holding an "EncryptedPackage" stream. */
function isEncryptedOffice(bytes: Uint8Array): boolean {
  const marker = 'EncryptedPackage';
  // The directory entry names are UTF-16LE.
  outer: for (let i = 0; i + marker.length * 2 <= bytes.length; i++) {
    for (let j = 0; j < marker.length; j++) {
      if (bytes[i + j * 2] !== marker.charCodeAt(j) || bytes[i + j * 2 + 1] !== 0) continue outer;
    }
    return true;
  }
  return false;
}

/**
 * Pick a reader by content first, extension second. A file called `.xlsx` that
 * is really a legacy .xls - which happens whenever somebody renames one - would
 * otherwise fail deep inside a zip reader with a message that helps nobody.
 *
 * Every failure here is turned into a sentence about the file - damaged,
 * password-protected, empty, too large - rather than a parser's internals.
 */
export async function readImportFile(file: File): Promise<SourceFile> {
  const name = file.name.toLowerCase();
  if (file.size === 0) throw new Error(`"${file.name}" is empty.`);
  if (file.size > MAX_IMPORT_FILE_BYTES) {
    throw new Error(
      `"${file.name}" is ${(file.size / 1048576).toFixed(0)} MB - more than this can open (${MAX_IMPORT_FILE_BYTES / 1048576} MB). Split it into smaller files, for example one per sheet.`,
    );
  }

  let raws: RawSheet[];
  try {
    if (/\.(csv|tsv|txt)$/.test(name)) {
      const sheet = parseCsv(await file.text());
      raws = [{ name: sheet.name, rowCount: sheet.rowCount, columnCount: MAX_COLUMNS, cell: (r, c) => sheet.cell(r, c) as CellValue, images: new Map() }];
    } else {
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (isOleFile(bytes)) {
        if (isEncryptedOffice(bytes)) {
          throw new ReadableError(
            `"${file.name}" is password-protected. Open it in Excel, remove the password (File → Info → Protect Workbook), save, and import that copy.`,
          );
        }
        const sheet = parseXls(bytes);
        raws = [{ name: sheet.name, rowCount: sheet.rowCount, columnCount: MAX_COLUMNS, cell: (r, c) => sheet.cell(r, c) as CellValue, images: new Map() }];
      } else if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
        raws = readWorkbook(bytes).sheets;
      } else {
        throw new ReadableError(
          'That file is not a spreadsheet this can read. Supported: .xlsx, .xlsm, .xlsb, .xls, .csv and .tsv.',
        );
      }
    }
  } catch (error) {
    if (error instanceof ReadableError) throw error;
    console.error('Could not read the workbook:', error);
    throw new Error(
      `"${file.name}" could not be opened - it may be damaged, still downloading, or not really the type its name says. Open it in Excel and save it again as .xlsx, then import that. (${error instanceof Error ? error.message : String(error)})`,
    );
  }

  if (raws.length === 0) throw new Error(`"${file.name}" has no sheets in it.`);
  return { fileName: file.name, sheets: raws.map(describeSheet) };
}

/** An error whose message is already written for the person reading it. */
class ReadableError extends Error {}

/** Find the heading row: the first of the top ten that names at least three known columns. */
function describeSheet(raw: RawSheet): SourceSheet {
  const width = Math.min(Math.max(raw.columnCount, 1), MAX_COLUMNS);
  let headerRow = 0;
  let headings: SourceHeading[] = [];
  for (let r = 1; r <= Math.min(10, raw.rowCount); r++) {
    const found: SourceHeading[] = [];
    for (let c = 1; c <= width; c++) {
      const text = clean(raw.cell(r, c));
      if (text) found.push({ col: c, text, key: headingKey(text) });
    }
    const known = found.filter((h) => FIELD_BY_ALIAS.has(h.key) || CLIENT_IGNORED[h.key] || NOT_IMPORTED[h.key]);
    if (known.length >= 3) {
      headerRow = r;
      headings = found;
      break;
    }
  }

  // Counted the way the import counts: a value in a column that is imported
  // by default, or in S.NO, or a picture. A stray label in an ignored column
  // is not a row of data.
  const counted = headings.filter((h) => defaultUse(h.key) !== 'ignore' || h.key === 'sno' || h.key === 'srno');
  let dataRows = 0;
  let pictures = 0;
  if (headerRow) {
    for (let r = headerRow + 1; r <= raw.rowCount; r++) {
      const images = raw.images.get(r)?.length ?? 0;
      if (images || counted.some((h) => clean(raw.cell(r, h.col)))) dataRows++;
      pictures += images;
    }
  }

  return { name: raw.name, rowCount: raw.rowCount, headerRow, headings, cell: raw.cell, images: raw.images, dataRows, pictures };
}

// --- rows -----------------------------------------------------------------

/**
 * Split "2170504 - BISHOP REAR ARMOR" into a code and a name.
 *
 * Kept deliberately identical to the server-side dataset importer: the sheet
 * uses three separators (hyphen, plain space, underscore) and not every code is
 * numeric - CP002 is a real mould.
 */
export function parseCodeName(codeName: string): { code: string; name: string } | null {
  // Dashes as explicit escapes, and the plain hyphen last in each class: an
  // en-dash written literally followed by a hyphen reads as a character range,
  // which is a syntax error and has already broken this line once.
  const trimmed = codeName.trim();
  if (!trimmed) return null;
  const m = /^([A-Za-z0-9][A-Za-z0-9/.]*)(?:[\s_–—-]+(.*))?$/.exec(trimmed);
  // No split we recognise: still a mould. Take the cell as written and let a
  // person tidy it later, rather than dropping the row unseen.
  if (!m) return { code: trimmed.toUpperCase(), name: trimmed };
  const code = m[1]!.toUpperCase();
  const rest = (m[2] ?? '').replace(/_/g, ' ');
  const name = clean(rest.replace(/^[\s,–—-]+/, '').replace(/[\s,–—-]+$/, ''));
  return { code, name: name || code };
}

/** One row as it goes to the server, plus the pictures that follow it. */
export interface BuiltRow extends ImportRowInput {
  images: SheetImage[];
  /** Pictures on the row in a format no browser can show, by name. */
  unsupportedImages: string[];
  /**
   * Further sheet rows folded into this mould: the other parts of a family
   * mould, written one row per part under a single S.NO.
   */
  partRows?: number[];
}

export interface BuiltImport {
  rows: BuiltRow[];
  /** Rows below the heading with anything in them, across the chosen sheets. */
  dataRowsSeen: number;
  /** Rows sent with no mould code; the server records each as a problem. */
  withoutCode: number;
  photos: number;
  unsupportedPhotos: number;
  /** Sheets that could not be read at all, and why. */
  sheetProblems: string[];
  /** Moulds written over several rows (one per part), and how many rows were folded in. */
  combined: { moulds: number; rows: number };
  /** Rows whose mould number is a placeholder such as "NA"; the server reports each. */
  placeholderCodes: number;
  /**
   * Size cells that hold no number - "MIRAROAD" typed into the size column.
   * Left out, so they are never engraved as a "Mold Dimension:"; the rest of
   * the row imports.
   */
  sizesLeftOut: Array<{ sheet: string; row: number; code: string; value: string }>;
}

/** A mould's name stays a name: past this, further parts are counted, not listed. */
const NAME_MAX = 480;

function joinParts(parts: string[]): string {
  let name = '';
  for (const [i, part] of parts.entries()) {
    const next = name ? `${name}, ${part}` : part;
    if (next.length > NAME_MAX) return `${name} (+${parts.length - i} more parts)`;
    name = next;
  }
  return name;
}

function yearOf(text: string): number | null {
  const m = /\b(19|20)\d{2}\b/.exec(text);
  return m ? Number(m[0]) : null;
}

export function buildRows(source: SourceFile, sheetNames: string[], use: (key: string) => ColumnUse): BuiltImport {
  const out: BuiltImport = {
    rows: [],
    dataRowsSeen: 0,
    withoutCode: 0,
    photos: 0,
    unsupportedPhotos: 0,
    sheetProblems: [],
    combined: { moulds: 0, rows: 0 },
    placeholderCodes: 0,
    sizesLeftOut: [],
  };
  /** Per mould that absorbed further rows: its parts and their cavity counts, in order. */
  const families = new Map<BuiltRow, { parts: string[]; cavities: string[] }>();

  for (const sheet of source.sheets.filter((s) => sheetNames.includes(s.name))) {
    if (!sheet.headerRow) {
      out.sheetProblems.push(`"${sheet.name}" has no heading row this can recognise.`);
      continue;
    }
    const columns = new Map<ImportField, number[]>();
    for (const h of sheet.headings) {
      const field = use(h.key);
      if (field === 'ignore') continue;
      columns.set(field, [...(columns.get(field) ?? []), h.col]);
    }
    if (!columns.has('code') && !columns.has('codeAndName')) {
      out.sheetProblems.push(
        `"${sheet.name}" has no column chosen as the mould code. Pick one in the column list (usually "MOLD NUMBER").`,
      );
      continue;
    }

    /** The first non-blank value among the columns chosen for a field. */
    const value = (r: number, field: ImportField): string => {
      for (const col of columns.get(field) ?? []) {
        const v = clean(sheet.cell(r, col));
        if (v) return v;
      }
      return '';
    };

    /** The size on a row, when it is one: a size has a number in it. */
    const sizeAt = (r: number, code: string): string => {
      const v = value(r, 'moldSize');
      if (!v || /\d/.test(v)) return v;
      out.sizesLeftOut.push({ sheet: sheet.name, row: r, code, value: v });
      return '';
    };

    // A row counts when something that is imported - or its S.NO - has a value.
    // Text only in an ignored column (a stray label in IMAGE) is not a mould.
    // A yes/no, a note, a location or a QR number on its own is not a mould
    // either: a stray "NO" under TOOL TRANSFER must not become part of the
    // mould above it.
    const counted = sheet.headings.filter((h) => {
      const field = use(h.key);
      if (field === 'toolTransfer' || field === 'notes' || field === 'location' || field === 'qrNumber') return false;
      return field !== 'ignore' || h.key === 'sno' || h.key === 'srno';
    });
    const snoCol = sheet.headings.find((h) => h.key === 'sno' || h.key === 'srno')?.col;
    /** The last mould this sheet produced, for rows that continue it. */
    let lastMould = null as BuiltRow | null;

    for (let r = sheet.headerRow + 1; r <= sheet.rowCount; r++) {
      const filled = counted.some((h) => clean(sheet.cell(r, h.col)));
      const images = sheet.images.get(r) ?? [];
      if (!filled && images.length === 0) continue;
      out.dataRowsSeen++;

      let code = '';
      let name = '';
      /** The name as written on this row, without falling back to the code. */
      let ownName = '';
      if (columns.has('code')) {
        code = value(r, 'code').toUpperCase();
        ownName = value(r, 'mouldName');
        name = ownName || code;
      } else {
        const split = parseCodeName(value(r, 'codeAndName'));
        code = split?.code ?? '';
        name = split?.name ?? '';
        ownName = split && split.name !== split.code ? split.name : '';
      }

      const masters: BuiltRow['masters'] = {};
      for (const [field] of columns) {
        if (!MASTER_FIELDS.has(field)) continue;
        const v = value(r, field);
        if (v) masters[field as ImportMasterField] = v;
      }

      const shown = images.filter((i) => i.mimeType);
      const unsupported = images.filter((i) => !i.mimeType).map((i) => i.formatName);
      out.photos += shown.length;
      out.unsupportedPhotos += unsupported.length;

      /*
       * A family mould - one tool making several parts - is written one row per
       * part: the S.NO on the first row only, the mould number repeated or left
       * blank below it. Those rows are this mould, not new ones and not
       * duplicates. Folded in: each part joins the name (as the Patalganga sheet
       * writes it, "SAFETY CLIP, TRIGGER, …"), its photos join the mould's, and
       * its values fill any blank. A repeat that has its own S.NO is a
       * different entry, and is still reported.
       */
      const sno = snoCol ? clean(sheet.cell(r, snoCol)) : '';
      // "NA" in the number column means no number: never the mould above, never one of its parts.
      const placeholder = !!code && isPlaceholderCode(code);
      if (placeholder) out.placeholderCodes++;
      const mould: BuiltRow | null = placeholder ? null : lastMould;
      if (
        snoCol !== undefined &&
        !sno &&
        mould &&
        ((code && code === mould.assetCode) || (!code && !ownName))
      ) {
        const family = families.get(mould) ?? {
          parts: mould.name && mould.name !== mould.assetCode ? [mould.name] : [],
          cavities: mould.cavities ? [mould.cavities] : [],
        };
        families.set(mould, family);
        if (ownName && !family.parts.some((p) => p.toUpperCase() === ownName.toUpperCase())) family.parts.push(ownName);
        if (ownName && value(r, 'cavities')) family.cavities.push(value(r, 'cavities'));
        mould.moldSize ||= sizeAt(r, mould.assetCode) || null;
        mould.manufactureYear ??= yearOf(value(r, 'manufactureYear'));
        mould.qrNumber ||= value(r, 'qrNumber') || null;
        mould.toolTransfer ??= yesNo(value(r, 'toolTransfer'));
        mould.notes ||= value(r, 'notes') || null;
        mould.location ||= value(r, 'location') || null;
        for (const [field] of columns) {
          if (!MASTER_FIELDS.has(field)) continue;
          const v = value(r, field);
          if (v && !mould.masters[field as ImportMasterField]) mould.masters[field as ImportMasterField] = v;
        }
        mould.images.push(...shown);
        mould.unsupportedImages.push(...unsupported);
        mould.photoCount = mould.images.length;
        mould.partRows = [...(mould.partRows ?? []), r];
        out.combined.rows++;
        continue;
      }

      if (!code) out.withoutCode++;

      const built: BuiltRow = {
        sheet: sheet.name,
        sheetRow: r,
        assetCode: code,
        name,
        cavities: value(r, 'cavities') || null,
        moldSize: sizeAt(r, code) || null,
        manufactureYear: yearOf(value(r, 'manufactureYear')),
        qrNumber: value(r, 'qrNumber') || null,
        toolTransfer: yesNo(value(r, 'toolTransfer')),
        notes: value(r, 'notes') || null,
        location: value(r, 'location') || null,
        masters,
        photoCount: shown.length,
        images: shown,
        unsupportedImages: unsupported,
      };
      out.rows.push(built);
      lastMould = code && !placeholder ? built : placeholder ? null : lastMould;
    }
  }

  for (const [mould, family] of families) {
    out.combined.moulds++;
    if (family.parts.length > 1) mould.name = joinParts(family.parts);
    // "2+2" is how the client's own sheets write the cavities of a family mould.
    if (family.cavities.length > 1) mould.cavities = family.cavities.join('+');
  }

  foldVariants(out.rows);
  return out;
}

/**
 * Two rows sharing a code but naming a variant - "R2 - FLAP", "R3 - FLAP" -
 * are two tools: the code is qualified with the variant, for both halves alike.
 * Any other repeat is left as it is, and the server reports it.
 */
function foldVariants(rows: BuiltRow[]): void {
  const counts = new Map<string, number>();
  for (const r of rows) if (r.assetCode && !isPlaceholderCode(r.assetCode)) counts.set(r.assetCode, (counts.get(r.assetCode) ?? 0) + 1);
  for (const r of rows) {
    if ((counts.get(r.assetCode) ?? 0) < 2) continue;
    const variant = /^(R\d+|[A-Z])\s*[-–-]\s*(.+)$/i.exec(r.name);
    if (!variant) continue;
    r.assetCode = `${r.assetCode}-${variant[1]!.toUpperCase()}`;
    r.name = clean(variant[2]!);
  }
}

/** The location a file most likely describes, from its name or a sheet title. */
export function suggestLocation<T extends { id: string; name: string }>(source: SourceFile, locations: T[]): T | null {
  const haystack = headingKey([source.fileName, ...source.sheets.map((s) => s.name)].join(' '));
  const hits = locations
    .filter((l) => headingKey(l.name).length >= 3 && haystack.includes(headingKey(l.name)))
    .sort((a, b) => headingKey(b.name).length - headingKey(a.name).length);
  // Two different places named in one file ("MOLD LIST FOR MIRA ROAD" inside
  // the Kandivali file) is a question for the person, not a guess.
  const distinct = new Set(hits.map((h) => h.id));
  return distinct.size === 1 ? hits[0]! : null;
}
